import React from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 15, 20, 25, 30];
export const DEFAULT_ROWS_PER_PAGE = 10;

/** Read persisted rows-per-page; falls back to DEFAULT_ROWS_PER_PAGE (10). */
export function readStoredRowsPerPage(
  storageKey,
  options = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  fallback = DEFAULT_ROWS_PER_PAGE
) {
  try {
    const saved = localStorage.getItem(storageKey);
    const parsed = parseInt(saved, 10);
    if (options.includes(parsed)) return parsed;
  } catch {
    /* ignore */
  }
  return fallback;
}

function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, "...", totalPages];
  }
  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

/** Standalone rows-per-page control (e.g. next to a table search box). */
export function RowsPerPageSelect({
  id = "table-rows-per-page",
  value,
  options = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  onChange,
  className = "",
}) {
  const safeValue = options.includes(Number(value))
    ? Number(value)
    : options.includes(DEFAULT_ROWS_PER_PAGE)
      ? DEFAULT_ROWS_PER_PAGE
      : options[0] || DEFAULT_ROWS_PER_PAGE;

  return (
    <div className={`pagination-rows-per-page${className ? ` ${className}` : ""}`}>
      <select
        id={id}
        className="pagination-rows-per-page__select"
        value={safeValue}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (options.includes(next)) onChange(next);
        }}
        aria-label="Rows"
        title="Rows"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

function TablePagination({
  currentPage,
  totalItems,
  rowsPerPage,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  onPageChange,
  onRowsPerPageChange,
  itemLabel = "items",
  rowsPerPageSelectId = "table-rows-per-page",
  showRowsPerPage = true,
}) {
  if (totalItems <= 0) {
    return null;
  }

  const safeRowsPerPage = Math.max(
    1,
    Number(rowsPerPage) || DEFAULT_ROWS_PER_PAGE || rowsPerPageOptions[0] || 10
  );
  const totalPages = Math.max(1, Math.ceil(totalItems / safeRowsPerPage));
  const startIndex = (currentPage - 1) * safeRowsPerPage;
  const startRow = startIndex + 1;
  const endRow = Math.min(startIndex + safeRowsPerPage, totalItems);
  const showPageNav = totalItems > safeRowsPerPage;

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <div
      className={`pagination${showRowsPerPage ? " pagination--with-rows-select" : ""}`}
    >
      <div className="pagination-main">
        {showPageNav ? (
          <>
            <button
              type="button"
              className="pagination-btn pagination-arrow"
              onClick={handlePrevious}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <FaChevronLeft />
            </button>
            <div className="page-numbers">
              {getPageNumbers(currentPage, totalPages).map((page, index) =>
                page === "..." ? (
                  <span key={`ellipsis-${index}`} className="page-number ellipsis">
                    ...
                  </span>
                ) : (
                  <span
                    key={page}
                    role="button"
                    tabIndex={0}
                    className={`page-number ${currentPage === page ? "active" : ""}`}
                    onClick={() => onPageChange(page)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onPageChange(page);
                      }
                    }}
                  >
                    {page}
                  </span>
                )
              )}
            </div>
            <button
              type="button"
              className="pagination-btn pagination-arrow"
              onClick={handleNext}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <FaChevronRight />
            </button>
          </>
        ) : null}
        <div className="pagination-info">
          {startRow}-{endRow} of {totalItems} {itemLabel}
        </div>
      </div>

      {showRowsPerPage ? (
        <RowsPerPageSelect
          id={rowsPerPageSelectId}
          value={safeRowsPerPage}
          options={rowsPerPageOptions}
          onChange={onRowsPerPageChange}
        />
      ) : null}
    </div>
  );
}

export default TablePagination;
